import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Avatar, IconButton, Tooltip } from '@material-ui/core';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import GitHubIcon from '@material-ui/icons/GitHub';
import axios from 'axios';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';

const background = `/assets/img/background.jpg`;
const EXPERIENCE_START_YEAR = 2010;
const LINKEDIN_URL = 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/';
const GITHUB_URL = 'https://github.com/ciap35/';

const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return true;
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const useStyles = makeStyles((theme) => ({
  section: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: `
      radial-gradient(circle at 20% 20%, rgba(57, 255, 20, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(0, 245, 255, 0.08) 0%, transparent 55%),
      url(${background})
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    color: 'var(--text-primary)',
    padding: theme.spacing(10, 0),
    marginTop: theme.spacing(8),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(45deg, rgba(0, 245, 255, 0.03), rgba(57, 255, 20, 0.025))',
      animation: 'backgroundShift 18s ease-in-out infinite',
      zIndex: 1,
    },
  },
  container: {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    position: 'relative',
    zIndex: 2,
  },
  mainContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(6),
    background: 'rgba(8, 10, 12, 0.90)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(57, 255, 20, 0.25)',
    borderRadius: '24px',
    padding: theme.spacing(6),
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(57, 255, 20, 0.08), 0 0 30px rgba(57, 255, 20, 0.10)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '38px',
      background: 'linear-gradient(180deg, rgba(57, 255, 20, 0.16), rgba(0, 0, 0, 0))',
      borderBottom: '1px solid rgba(57, 255, 20, 0.18)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, rgba(0,0,0,0) 3px, rgba(0,0,0,0) 6px)',
      opacity: 0.15,
      mixBlendMode: 'overlay',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      textAlign: 'center',
      gap: theme.spacing(4),
    },
  },
  consoleHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    zIndex: 2,
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.95rem',
    letterSpacing: '0.03em',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  consoleDots: {
    display: 'flex',
    gap: theme.spacing(1),
    marginRight: theme.spacing(2),
    '& span': {
      width: 10,
      height: 10,
      borderRadius: 999,
      display: 'inline-block',
      background: 'rgba(255, 255, 255, 0.18)',
    },
  },
  consoleTitle: {
    color: 'var(--accent-green)',
    textShadow: '0 0 12px rgba(57, 255, 20, 0.35)',
  },
  avatarSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '280px',
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
    },
  },
  socialRow: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.25),
  },
  socialButton: {
    width: 40,
    height: 40,
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(57, 255, 20, 0.18)',
    boxShadow: '0 0 0 1px rgba(57, 255, 20, 0.06), 0 10px 22px rgba(0, 0, 0, 0.25)',
    transition: 'transform 0.2s ease, background 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      background: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'rgba(0, 245, 255, 0.28)',
    },
  },
  socialIcon: {
    fontSize: '1.25rem',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  connectionPanel: {
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: 340,
    position: 'relative',
    borderRadius: 16,
    padding: theme.spacing(1.75, 2),
    background: 'rgba(0, 0, 0, 0.26)',
    boxShadow: '0 0 0 1px rgba(57, 255, 20, 0.16), 0 0 26px rgba(57, 255, 20, 0.10)',
    overflow: 'hidden',
  },
  connectionFrame: {
    position: 'relative',
    padding: theme.spacing(2, 2.5),
    borderRadius: 14,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      opacity: 0.95,
      background:
        // top-left
        'linear-gradient(rgba(57,255,20,0.9) 0 0) left top / 22px 2px no-repeat,' +
        'linear-gradient(rgba(57,255,20,0.9) 0 0) left top / 2px 22px no-repeat,' +
        // top-right
        'linear-gradient(rgba(57,255,20,0.9) 0 0) right top / 22px 2px no-repeat,' +
        'linear-gradient(rgba(57,255,20,0.9) 0 0) right top / 2px 22px no-repeat,' +
        // bottom-left
        'linear-gradient(rgba(57,255,20,0.9) 0 0) left bottom / 22px 2px no-repeat,' +
        'linear-gradient(rgba(57,255,20,0.9) 0 0) left bottom / 2px 22px no-repeat,' +
        // bottom-right
        'linear-gradient(rgba(57,255,20,0.9) 0 0) right bottom / 22px 2px no-repeat,' +
        'linear-gradient(rgba(57,255,20,0.9) 0 0) right bottom / 2px 22px no-repeat',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      opacity: 0.14,
      background: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, rgba(0,0,0,0) 3px, rgba(0,0,0,0) 6px)',
      mixBlendMode: 'overlay',
    },
  },
  connectionBar: {
    position: 'absolute',
    right: 14,
    top: '50%',
    width: 2,
    height: '58%',
    transform: 'translateY(-50%)',
    borderRadius: 999,
    background: 'rgba(57,255,20,0.85)',
    boxShadow: '0 0 14px rgba(57,255,20,0.35)',
    animation: '$barPulse 1.6s ease-in-out infinite',
    pointerEvents: 'none',
  },
  connectionText: {
    position: 'relative',
    zIndex: 1,
    fontFamily: '"JetBrains Mono", monospace',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    textAlign: 'center',
    color: 'rgba(57,255,20,0.95)',
    textShadow: '0 0 16px rgba(57,255,20,0.25)',
    lineHeight: 1.15,
    fontWeight: 800,
    fontSize: 'clamp(0.9rem, 2.1vw, 1.05rem)',
    userSelect: 'none',
  },
  avatar: {
    width: theme.spacing(24),
    height: theme.spacing(24),
    border: '2px solid rgba(57, 255, 20, 0.35)',
    boxShadow: '0 0 22px rgba(57, 255, 20, 0.22), 0 15px 35px rgba(0, 0, 0, 0.35)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 0 34px rgba(57, 255, 20, 0.35), 0 20px 40px rgba(0, 0, 0, 0.45)',
    },
    [theme.breakpoints.down('md')]: {
      width: theme.spacing(20),
      height: theme.spacing(20),
    },
  },
  textSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    position: 'relative',
    zIndex: 2,
  },
  title: {
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 800,
    fontSize: '2.25rem',
    color: 'var(--accent-green)',
    textShadow: '0 0 18px rgba(57, 255, 20, 0.35)',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.9rem',
    },
  },
  divider: {
    height: '2px',
    background: 'linear-gradient(90deg, transparent, rgba(57,255,20,0.55), transparent)',
    border: 'none',
    margin: theme.spacing(2, 0),
    animation: 'pulse 2s ease-in-out infinite',
  },
  content: {
    fontSize: '1.05rem',
    lineHeight: '1.75',
    color: 'rgba(220, 255, 220, 0.92)',
    fontFamily: '"JetBrains Mono", monospace',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.22)',
    border: '1px solid rgba(57, 255, 20, 0.14)',
    transition: 'all 0.25s ease',
    textAlign: 'left',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.30)',
      borderColor: 'rgba(57, 255, 20, 0.22)',
      boxShadow: '0 0 0 1px rgba(57,255,20,0.08), 0 0 18px rgba(57,255,20,0.10)',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      padding: theme.spacing(1.5),
    },
  },
  prompt: {
    color: 'rgba(57, 255, 20, 0.95)',
  },
  cursor: {
    display: 'inline-block',
    width: 10,
    height: '1em',
    marginLeft: 6,
    background: 'rgba(57, 255, 20, 0.85)',
    animation: '$blink 1s steps(1, end) infinite',
    transform: 'translateY(2px)',
  },
  // Animaciones
  '@keyframes backgroundShift': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.8 },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.6 },
    '50%': { opacity: 1 },
  },
  '@keyframes blink': {
    '0%, 49%': { opacity: 1 },
    '50%, 100%': { opacity: 0 },
  },
  '@keyframes barPulse': {
    '0%, 100%': { opacity: 0.55 },
    '50%': { opacity: 1 },
  },
}));

const About = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const experienceYears = Math.max(0, new Date().getFullYear() - EXPERIENCE_START_YEAR);
  const [githubProfile, setGithubProfile] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    axios.get('https://api.github.com/users/ciap35')
      .then(response => {
        setGithubProfile(response.data);
      })
      .catch(error => {
        console.error('Error fetching GitHub profile', error);
      });
  }, []);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    gsap.registerPlugin(ScrollTrigger);

    const root = rootRef.current;
    if (!root) return;

    const lines = root.querySelectorAll('[data-console-line]');

    const ctx = gsap.context(() => {
      gsap.fromTo(
        lines,
        { autoAlpha: 0, x: -10 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.35,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: {
            trigger: root,
            start: 'top 75%',
          },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section className={classes.section} ref={rootRef}>
      <div className={classes.container}>
        <div className={classes.mainContent}>
          <div className={classes.consoleHeader} aria-hidden="true">
            <div className={classes.consoleDots}>
              <span />
              <span />
              <span />
            </div>
            <span className={classes.consoleTitle}>CIAP_TERMINAL</span>
            <span style={{ opacity: 0.75 }}>&nbsp;—&nbsp;about.exe</span>
          </div>

          <div className={classes.avatarSection}>
            {githubProfile && (
              <Avatar
                alt="GitHub Profile Picture"
                src={githubProfile.avatar_url}
                className={classes.avatar}
              />
            )}

            <div className={classes.socialRow}>
              <Tooltip title="LinkedIn" placement="bottom">
                <IconButton
                  className={classes.socialButton}
                  component="a"
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon className={classes.socialIcon} />
                </IconButton>
              </Tooltip>
              <Tooltip title="GitHub" placement="bottom">
                <IconButton
                  className={classes.socialButton}
                  component="a"
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="GitHub"
                >
                  <GitHubIcon className={classes.socialIcon} />
                </IconButton>
              </Tooltip>
            </div>

            <div className={classes.connectionPanel} aria-label={t('about.connection.aria')}>
              <div className={classes.connectionFrame}>
                <div className={classes.connectionBar} aria-hidden="true" />
                <div className={classes.connectionText}>
                  <div>{t('about.connection.line1')}</div>
                  <div>{t('about.connection.line2')}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className={classes.textSection}>
            <Typography variant="h1" className={classes.title}>
              {t('about.title')}
            </Typography>
            <hr className={classes.divider} />
            
            <Typography variant="body1" className={classes.content} data-console-line>
              <span className={classes.prompt}>ciap@portfolio</span>:<span className={classes.prompt}>~</span>$ whoami
              <br />
              {t('about.whoami', { years: experienceYears })}
            </Typography>
            
            <Typography variant="body1" className={classes.content} data-console-line>
              <span className={classes.prompt}>ciap@portfolio</span>:<span className={classes.prompt}>~</span>$ cat background.txt
              <br />
              {t('about.background')}
            </Typography>
            
            <Typography variant="body1" className={classes.content} data-console-line>
              <span className={classes.prompt}>ciap@portfolio</span>:<span className={classes.prompt}>~</span>$ tail -n 1 now.log
              <br />
              {t('about.now')}
              <span className={classes.cursor} aria-hidden="true" />
            </Typography>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
